/**
 * LoginPage — full-page login screen with logo and form.
 */

import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Card, CardContent, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import LoginForm from "@/components/auth/LoginForm";
import { useAuth } from "@/hooks/useAuth";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();
  const { t } = useTranslation("auth");
  const { t: tc } = useTranslation("common");
  const from = (location.state as { from?: { pathname?: string; search?: string } } | null)?.from;
  const target = from?.pathname && from.pathname !== "/login" ? `${from.pathname}${from.search ?? ""}` : "/";

  useEffect(() => {
    if (isAuthenticated) navigate(target, { replace: true });
  }, [isAuthenticated, navigate, target]);

  const handleLogin = async (credentials: { username: string; password: string }) => {
    const result = await login(credentials);
    if (result.mustChangePassword) {
      navigate("/change-password", { replace: true, state: { tempPassword: credentials.password } });
    } else {
      navigate(target, { replace: true });
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "background.default", p: 2 }}>
      <Card sx={{ maxWidth: 420, width: "100%", mx: "auto" }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Box
              component="img"
              src="/ksena-stavby-logo.png"
              alt="Stavby KSENA"
              sx={{ width: "100%", maxWidth: 180, height: "auto", display: "block", mx: "auto", mb: 1.5 }}
            />
            <Typography variant="h5" component="h1">{tc("appTitle")}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{t("appSubtitle")}</Typography>
          </Box>
          <Typography variant="h6" component="h2" sx={{ mb: 2, textAlign: "center" }}>{t("loginTitle")}</Typography>
          <LoginForm onSubmit={handleLogin} />
        </CardContent>
      </Card>
    </Box>
  );
}
